import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import { makeStyles, ThemeProvider } from "@material-ui/core/styles";
import {
  createTheme,
  ThemeProvider as Mui5ThemeProvider,
} from "@mui/material/styles";
import {
  createGenerateClassName,
  StylesProvider,
  ThemeProvider as StylesThemeProvider,
} from "@mui/styles";
import AdminLayout from "@layouts/admin/Admin";
import loadTheme from "@assets/jss/theme";
import CssBaseline from "@material-ui/core/CssBaseline";
import Loading from "@components/loading/Loading";
import classNames from "classnames";
import LayoutProvider from "@providers/LayoutProvider";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import ConfirmationProvider from "@providers/ConfirmationProvider";
import RoutesProvider from "providers/RoutesProvider";
import BookingProvider from "@providers/BookingProvider";
import Login from "pages/auth/Login";
import { ProtectedRoute } from "@components/protectedroute/protectedroute";
import Forgot from "pages/auth/Forgot";
import Reset from "pages/auth/Reset";

const mui5ClassName = createGenerateClassName({ seed: "mui5-" });

const useStyles = makeStyles({
  app: {
    display: "block",
    visibility: "hidden",
    opacity: 0,
    transition: "visibility 0s, opacity 0.2s linear",
  },
  active: {
    visibility: "visible",
    opacity: 1,
  },
});

const App = () => {
  const [theme, setTheme] = useState(null);
  const [themeLoaded, setThemeLoaded] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    const effectTheme = async () => {
      const t = await loadTheme();
      setTheme(t);
      // set time out so that transiation effect works
      setTimeout(() => {
        setThemeLoaded(true);
      }, 1);
    };
    effectTheme();
  }, []);

  const mui5Theme = useMemo(() => {
    if (!theme) {
      return null;
    }
    return createTheme({
      breakpoints: {
        values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
      },
      palette: {
        mode: theme.palette.type || "light",
        primary: { main: theme.palette.primary.main },
        secondary: { main: theme.palette.secondary.main },
        background: {
          default: theme.palette.background.default,
          paper: theme.palette.background.paper,
        },
        text: {
          primary: theme.palette.text.primary,
          secondary: theme.palette.text.secondary,
        },
      },
      typography: {
        fontFamily: theme.typography.fontFamily,
        subtitle1: theme.typography.subtitle1,
        h1: theme.typography.h1,
        h2: theme.typography.h2,
        h3: theme.typography.h3,
        h4: theme.typography.h4,
        h5: theme.typography.h5,
        h6: theme.typography.h6,
      },
      shape: { borderRadius: theme.shape.borderRadius },
      spacing: theme.spacing(1),
    });
  }, [theme]);

  if (!theme || !mui5Theme) {
    return <Loading />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Mui5ThemeProvider theme={mui5Theme}>
        <StylesProvider generateClassName={mui5ClassName}>
          <StylesThemeProvider theme={mui5Theme}>
      <DndProvider backend={HTML5Backend}>
        <RoutesProvider>
          <LayoutProvider>
            {/* <NotificationProvider> */}
            <ConfirmationProvider>
              <BookingProvider>
                <CssBaseline />
                <Router>
                  <span
                    className={classNames(classes.app, {
                      [classes.active]: themeLoaded,
                    })}
                  >
                    <Switch>
                      <Route path="/login" exact component={Login} />
                      <Route path="/forgot" exact component={Forgot} />
                      <Route
                        path="/reset-password/:code"
                        exact
                        component={Reset}
                      />
                      <Route exact path="/">
                        <Redirect to="/bookings" />
                      </Route>
                      <ProtectedRoute path="/" component={AdminLayout} />
                    </Switch>
                  </span>
                </Router>
              </BookingProvider>
            </ConfirmationProvider>
            {/* </NotificationProvider> */}
          </LayoutProvider>
        </RoutesProvider>
      </DndProvider>
          </StylesThemeProvider>
        </StylesProvider>
      </Mui5ThemeProvider>
    </ThemeProvider>
  );
};

export default App;
